/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { memo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import ImageComponent from "@/components/ui/image";
import { UniversalFoodIndicator } from "@/components/common/UniversalFoodIndicators";
import Link from "next/link";
import { Product } from "@/types/product";
import { getProductDietaryCategories } from "@/utils/product/detail";
import { getAllDietaryCategories } from "@/services/api/product/details";
import { sendGAEvent } from "@next/third-parties/google";
import { generateProductAltText } from "@/utils/seo";

// import Favorite from "@/components/Favorite";

interface ProductCardProps {
  product: Product;
  productPage?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = memo(
  ({ product, productPage = false }) => {
    const { data: allDietaryCategories } = useQuery({
      queryKey: ["dietary-categories"],
      queryFn: () => getAllDietaryCategories(),
    });

    const dietaryCategories = getProductDietaryCategories({
      allDietaryCategories: allDietaryCategories || [],
      productDietaryCategoryIds: product.dietary_categories || [],
    });
    // Convert object properties to individual constants
    const id = product.id;
    const image = product.product_urls?.[0] || "";
    const title = product.name;
    const price = Number(
      parseFloat(Number(product.base_price_amount).toFixed(2).toString()),
    );
    const vendor =
      product.vendor_details?.business_name ||
      product.metadata?.vendor_name ||
      "Unknown";
    const type =
      typeof product.dietary_type === "string"
        ? (product.dietary_type?.toLowerCase() as "" | "veg" | "non_veg")
        : "";
    const pillText = product.is_best_product ? "Best Seller" : "";
    const vendorId = product.vendor_details?.id?.toString() || "";

    const isInStock = product.in_stock;
    const vendorDiscountText = product?.vendor_discount_text || "";

    const overlayText = ""; // Not present in the current Product interface
    const renderCard = (children: React.ReactNode) => {
      return (
        <Link
          href={`/${product.vendor_details?.slug}/product/${(product as any).slug}`}
          className={`${!isInStock ? "opacity-80" : ""}`}
          onClick={() => {
            sendGAEvent("event", "select_item", {
              currency: "INR",
              value: product.base_price_amount.toString(),
              items: [
                {
                  item_id: String(product.id),
                  item_name: product.name,
                  item_brand: product.vendor_details?.slug || "",
                  item_category: product.product_type_name,
                  item_category2: product.product_subtype_details?.name,
                  price: Number(product.base_price_amount),
                  quantity: 1,
                },
              ],
            });
          }}
        >
          {children}
        </Link>
      );
    };

    const renderOpaqueTitle = () => {
      if (!isInStock) return "SOLD OUT";
      if (vendorDiscountText) return vendorDiscountText;
      return "";
    };
    const opaqueTitle = (() => {
      if (!isInStock) return "SOLD OUT";
    })();
    const offerTitle = (() => {
      if (vendorDiscountText) return vendorDiscountText;
    })();

    const renderVendorSection = useCallback(
      () =>
        vendor && (
          <div>
            <div
              className="relative bg-gradient-to-r from-[#FCFFFF] via-[#FCFFFF] to-[#FCFFFF] lg:px-4 px-0 py-3 mt-2 lg:block hidden"
              style={{
                borderTop: "2px dashed #d9d9d9",
                borderImage:
                  "repeating-linear-gradient(to right, #d9d9d9 0, #d9d9d9 4px, transparent 8px, transparent 6px) 1",
              }}
            >
              <Link
                className="text-[#037a6a]  lg:text-lg text-xs font-light font-outfit flex items-center"
                href={`/${product.vendor_details?.slug}/${vendorId}`}
              >
                <span className="truncate">By {vendor}</span>
                <span className="ml-1">→</span>
              </Link>
              <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-[#EFFEFC] clip-triangle"></div>
            </div>
            <div className="block lg:hidden relative bg-gradient-to-r from-[#FCFFFF] via-[#FCFFFF] to-[#FCFFFF] lg:px-4 pr-2 py-3 mt-2 border-t border-dashed border-[#d9d9d9]">
              <Link
                className="text-[#037a6a]  text-sm font-light flex items-center font-outfit"
                href={`/${product.vendor_details?.slug}/${vendorId}`}
              >
                <span className="truncate">By {vendor}</span>
                <span className="ml-1">→</span>
              </Link>
              <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-[#EFFEFC] clip-triangle"></div>
            </div>
          </div>
        ),
      [product.vendor_details?.slug, vendorId, vendor],
    );

    const displayTitle =
      typeof title === "string" && title.split(" ").length > 5
        ? `${title.split(" ").slice(0, 5).join(" ")}...`
        : title;

    if (product.visible_in_listings === false) {
      return null;
    }

    if (productPage) {
      return (
        <div key={title} className="bg-white rounded-lg overflow-hidden p-2">
          {renderCard(
            <div className="flex w-full h-28 md:h-[232px]">
              {/* Left side - Image */}
              <div className="md:w-1/2 w-36 lg:w-56 rounded-lg overflow-hidden">
                <div
                  className="rounded-lg w-full relative h-full cursor-pointer"
                  // You can add onClick logic here if needed
                >
                  <ImageComponent
                    width={500}
                    height={500}
                    quality={80}
                    src={image}
                    alt={
                      product.metadata?.alt_text ||
                      product.metadata?.image_alt ||
                      generateProductAltText(
                        product.name,
                        product.vendor_details?.business_name ||
                          product.metadata?.vendor_name,
                      )
                    }
                    sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 250px"
                    className="w-full h-full object-cover transition-transform rounded-lg duration-300 hover:scale-105"
                  />
                  {/* Tag */}
                  {pillText && (
                    <div
                      className="absolute top-2 right-2 bg-white md:px-2 md:py-1 px-[6px] py-[2px] rounded-full md:text-sm text-xs font-semibold text-[#079ea6] border font-outfit
                     italic border-yellow-400 shadow"
                    >
                      {pillText}
                    </div>
                  )}
                  {overlayText && (
                    <div className="absolute bg-gradient-to-t from-black to-transparent shadow-lg bottom-0 left-0 right-0 text-white px-4 py-2 lg:text-lg text-xs font-semibold text-left font-serif whitespace-nowrap ">
                      {overlayText}
                    </div>
                  )}

                  {!isInStock && (
                    <>
                      {/* Dark overlay on image only */}
                      <div className={`absolute inset-0 z-20 rounded-lg`} />
                      {/* Gradient bar with text */}
                      <div className="absolute left-0 bottom-0 w-full bg-gradient-to-t from-[#252525]   h-[40px] px-3 py-1 flex flex-col items-center justify-end rounded-b-[12px] z-30">
                        <span className="font-serif md:text-[20px] text-base font-semibold md:font-medium text-white leading-[27px] uppercase">
                          {renderOpaqueTitle()}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {/* Right side - Details */}
              <div className="w-1/2  pl-3 lg:pl-4 h-full flex flex-col justify-between items-start">
                <div className="w-full h-full flex flex-col ">
                  <h3 className="lg:text-xl text-sm font-medium text-[#303030] lg:truncate font-lora mb-2 min-h-8">
                    {displayTitle}
                  </h3>
                  <div className="lg:text-lg text-base font-outfit font-medium text-[#2C2C2C]">
                    ₹{price}
                  </div>
                </div>
                <div className="flex lg:flex-col flex-row max-lg:items-end justify-end max-lg:space-x-1 h-full">
                  <UniversalFoodIndicator
                    type={type}
                    className="lg:p-2 lg:pt-8"
                  />
                  <div className="flex flex-nowrap text-nowrap gap-2 ">
                    {(dietaryCategories ?? [])
                      .slice(0, 2)
                      .map((name, index) => (
                        <span
                          key={index}
                          className="text-[#5a5a5a] font-outfit italic font-medium lg:text-base text-xs border-r-[1px] border-[#d9d9d9] lg:pr-2 pr-1"
                        >
                          {name.name}
                        </span>
                      ))}
                    {(dietaryCategories ?? []).length > 2 && (
                      <span className="text-[#5a5a5a] font-outfit italic font-medium lg:text-base text-xs ">
                        ...
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>,
          )}
          {/* Vendor Section */}
          <div className="relative bg-gradient-to-r from-[#FCFFFF] via-[#FCFFFF] to-[#FCFFFF] lg:px-4 px-2 py-3 border-[#d9d9d9] border-dashed border-t-[1px] mt-2">
            <Link
              className="text-[#037a6a]  lg:text-xl text-xs font-medium flex items-center w-full"
              href={`/${product.vendor_details?.slug}/${vendorId}`}
            >
              <span className="truncate">By {vendor}</span>
              <span className="ml-1 text-base">→</span>
            </Link>
            <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-[#EFFEFC] clip-triangle"></div>
          </div>
        </div>
      );
    }
    return (
      <>
        {/* desktop view */}
        <div className="hidden md:flex flex-col w-full">
          <div
            key={id}
            className={`bg-white rounded-lg overflow-hidden p-2 w-full relative flex flex-col tracking-tight`}
          >
            {renderCard(
              <>
                {/* Image Section */}
                <div className="w-full lg:h-56 h-32 relative rounded-lg overflow-hidden">
                  <ImageComponent
                    width={320}
                    height={230}
                    quality={100}
                    src={image || "/placeholder.svg"}
                    alt={
                      product.metadata?.alt_text ||
                      product.metadata?.image_alt ||
                      generateProductAltText(
                        product.name,
                        product.vendor_details?.business_name ||
                          product.metadata?.vendor_name,
                      )
                    }
                    sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 250px"
                    className="w-full h-full object-cover transition-transform rounded-lg duration-300 hover:scale-105"
                  />
                  {/* Tag */}
                  {pillText && (
                    <div className="absolute top-2 right-2 bg-white md:px-2 md:py-1 px-[6px] py-[2px] rounded-full md:text-sm text-base font-semibold text-[#079ea6] border font-outfit italic border-yellow-400 shadow">
                      {pillText}
                    </div>
                  )}
                  {overlayText && (
                    <div className="absolute bg-gradient-to-t from-black to-transparent shadow-lg bottom-0 left-0 w-full text-left right-0 text-white px-4 py-2 lg:text-lg text-xs font-semibold font-serif">
                      {overlayText}
                    </div>
                  )}
                  {/* Unavailable overlay and label (image only) */}
                  {(!isInStock || vendorDiscountText) && (
                    <>
                      {/* Dark overlay on image only */}
                      <div className={`absolute inset-0 z-20 rounded-lg`} />
                      {/* Gradient bar with text */}
                      <div className="absolute left-0 bottom-0 w-full bg-gradient-to-t from-[#252525] h-[40px] px-3 py-1 flex flex-col items-center justify-end rounded-b-[12px] z-30">
                        <span className="font-serif text-[20px] font-medium text-white leading-[27px] uppercase text-center">
                          {renderOpaqueTitle()}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                {/* Details Section */}
                <div className="pt-4 max-sm:py-2">
                  {/* <div className="flex justify-between  mb-2 items-center"> */}
                  {/* Hiding Rating for now*/}
                  {/* <div className="flex items-center gap-1 mb-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="lg:w-6 lg:h-6 w-4 h-4 text-[#F9AD09]"
                >
                  <path d="M12 2.5c.3 0 .6.2.7.5l2.6 5.3 5.8.8c.3 0 .6.2.7.5.1.3 0 .6-.2.8l-4.2 4.1 1 5.7c.1.3-.1.6-.4.8-.3.2-.6.2-.9 0L12 18.9l-5.2 2.8c-.3.2-.6.2-.9 0s-.5-.5-.4-.8l1-5.7-4.2-4.1c-.2-.2-.3-.5-.2-.8.1-.3.4-.5.7-.5l5.8-.8 2.6-5.3c.2-.3.5-.5.8-.5z" />
                </svg>
                <span className="lg:text-base text-xs font-lato font-semibold text-[#1E0C42]">
                  {rating}
                </span>
                <span className="text-[#037a6a] lg:text-base text-xs font-lato font-semibold">
                  ({totalUsers})
                </span>
              </div> */}
                  {/* <Favorite
                className="!bg-[#E6E6E6] lg:p-[5px] ml-auto"
                heartclassName="lg:w-[16px] lg:h-[16px]"
              /> */}
                  {/* </div> */}
                  <h3
                    className="lg:text-lg text-xs font-medium text-[#303030] lg:truncate font-lora mb-2 min-h-8"
                    title={title}
                  >
                    {title}
                  </h3>
                  <div className="lg:text-lg text-xs font-outfit font-semibold text-[#2C2C2C]">
                    ₹{price}
                  </div>
                  {(type ||
                    (dietaryCategories && dietaryCategories.length > 0)) && (
                    <div className="flex flex-nowrap text-nowrap gap-2 mt-2 items-center justify-start h-8">
                      <UniversalFoodIndicator type={type} className="" />
                      {dietaryCategories
                        ?.slice(0, 2)
                        .map(({ name }: { name: string }, index: number) => (
                          <span
                            key={index}
                            className={`text-[#5a5a5a] font-outfit italic font-medium lg:text-lg text-sm ${index + 1 !== dietaryCategories?.length && "border-r-[1px] border-[#d9d9d9]"} lg:pr-2 pr-1`}
                          >
                            {name}
                          </span>
                        ))}
                      {dietaryCategories?.length > 2 && (
                        <span
                          className="italic"
                          title={dietaryCategories
                            ?.map(({ name }: { name: string }) => name)
                            .join(", ")}
                        >
                          ...
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </>,
            )}
            {/* Vendor Section */}
            {renderVendorSection()}
          </div>
        </div>
        {/* mobile */}
        <div className="flex md:hidden flex-col items-start w-full">
          <div className="bg-white rounded-lg overflow-hidden p-[6px] relative flex flex-col">
            {renderCard(
              <>
                <div className="w-full h-40 relative rounded-lg overflow-hidden">
                  <ImageComponent
                    width={320}
                    height={160}
                    quality={80}
                    src={image || "/placeholder.svg"}
                    alt={
                      product.metadata?.alt_text ||
                      product.metadata?.image_alt ||
                      generateProductAltText(
                        product.name,
                        product.vendor_details?.business_name ||
                          product.metadata?.vendor_name,
                      )
                    }
                    sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 250px"
                    className="w-full h-full object-cover transition-transform rounded-lg duration-300 hover:scale-105"
                  />
                  {/* Tag */}
                  {pillText && (
                    <div className="absolute top-2 right-2 bg-white px-2 rounded-full md:text-base font-medium text-[#079ea6] border-[1.16px] border-[#EAD520] shadow font-outfit italic text-xs">
                      {pillText}
                    </div>
                  )}
                  {overlayText && (
                    <div className="absolute bg-gradient-to-t from-black to-transparent shadow-lg bottom-0 left-0 right-0 text-white px-4 py-2 md:text-xl font-medium font-serif text-xs">
                      {overlayText}
                    </div>
                  )}
                  {/* Unavailable overlay and label (image only) */}
                  {(!isInStock || vendorDiscountText) && (
                    <>
                      {/* Dark overlay on image only */}
                      <div className={`absolute inset-0 z-20 rounded-lg`} />
                      {/* Gradient bar with text */}
                      <div className="absolute left-0 bottom-0 w-full bg-gradient-to-t from-[#252525] h-[20px] px-3 py-1 flex flex-col items-center justify-end rounded-b-[12px] z-30">
                        {/* If opaqueTitle exists ("Unavailable" or "SOLD OUT"), render it */}
                        {opaqueTitle && (
                          <span className="font-serif text-sm font-semibold text-white leading-[100%] uppercase tracking-normal">
                            {opaqueTitle}
                          </span>
                        )}

                        {/* If opaqueTitle DOES NOT exist, render the offer title */}
                        {!opaqueTitle && offerTitle && (
                          <span className="font-outfit text-sm font-medium text-white leading-[100%] uppercase tracking-tighter">
                            {offerTitle.replace(/\sUSE\s.*/i, "")}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="py-4 px-1 max-sm:py-2">
                  {/* <div className="flex justify-between  mb-2 items-center">
              <div className="flex gap-1 font-lato font-semibold md:text-base text-[10px] items-center">
                <ImageComponent
                  src={starImg}
                  alt="rating"
                  width={20}
                  height={20}
                  className="lg:w-4 lg:h-4 w-[10px] h-[10px]"
                />
                <span className=" text-[#1E0C42]">{rating}</span>
                <span className="text-[#037A6A] ">({totalUsers})</span>
              </div>
              <Favorite
                className="!bg-[#E6E6E6] lg:p-[5px]"
                heartclassName="lg:w-[16px] lg:h-[16px]"
              />
            </div> */}
                  <h3
                    className="md:text-base text-sm font-medium font-lora text-[#303030] truncate mb-2"
                    title={title}
                  >
                    {title}
                  </h3>
                  <div className="md:text-lg font-outfit font-medium text-[#2c2c2c] text-base">
                    ₹{price}
                  </div>
                  {(type ||
                    (dietaryCategories && dietaryCategories.length > 0)) && (
                    <div className="flex flex-nowrap text-nowrap gap-2 mt-2 items-center justify-start h-4">
                      <UniversalFoodIndicator type={type} className="" />
                      {dietaryCategories
                        ?.slice(0, 2)
                        .map(({ name }: { name: string }, index: number) => (
                          <span
                            key={index}
                            className="italic font-outfit text-[#5a5a5a] md:text-lg text-[9px]"
                          >
                            {name}
                          </span>
                        ))}
                      {dietaryCategories?.length > 2 && (
                        <span
                          className="italic"
                          title={dietaryCategories
                            ?.map(({ name }: { name: string }) => name)
                            .join(", ")}
                        >
                          ...
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </>,
            )}
            {/* Vendor Section */}
            {renderVendorSection()}
          </div>
        </div>
      </>
    );
  },
);

ProductCard.displayName = "ProductCard";

export default ProductCard;
